import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, LifeBuoy, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bell campanella per notifiche in-app (Fase 2 Task 2.4).
 * Mostrata solo per non-clienti (Agente, TopAgent, Admin, SuperAdmin).
 * Poll ogni 45s del count non-lette. Al click apre popover con lista + mark as read.
 */
export default function NotificationBell() {
  const { getAuthHeader, user } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const isNonClient = user && !['cliente', 'cliente_smart'].includes(user.role);

  const fetchCount = useCallback(async () => {
    if (!isNonClient) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeader() });
      setCount(res.data?.count || 0);
    } catch { /* silent */ }
  }, [getAuthHeader, isNonClient]);

  const fetchList = useCallback(async () => {
    if (!isNonClient) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers: getAuthHeader() });
      setItems(res.data || []);
    } catch { /* silent */ }
  }, [getAuthHeader, isNonClient]);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 45000);
    return () => clearInterval(id);
  }, [fetchCount]);

  const handleOpen = (o) => {
    setOpen(o);
    if (o) fetchList();
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() });
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setCount(c => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  if (!isNonClient) return null;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
          title="Notifiche"
          data-testid="btn-notifiche"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifiche-badge">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 max-h-[70vh] overflow-y-auto">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <h4 className="font-semibold text-sm">Notifiche</h4>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        {items.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-400">Nessuna notifica</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map(n => (
              <li
                key={n.id}
                onClick={() => !n.is_read && markRead(n.id)}
                className={`p-3 cursor-pointer hover:bg-slate-50 ${!n.is_read ? 'bg-emerald-50/40' : ''}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <LifeBuoy className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800">
                      {n.from_user_name || 'Cliente'} — richiesta supporto
                    </p>
                    <p className="text-xs text-slate-600 line-clamp-3 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString('it-IT')}</p>
                  </div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1" />}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
